import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmtMoney } from "../lib/currency";
import { paymentMethodLabel } from "../lib/paymentMethods";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  Car,
  PackageOpen,
  PackageCheck,
  Plus,
  Loader2,
  ShoppingCart,
} from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { defaultCurrency, salesStockEnabled } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [storages, setStorages] = useState([]);
  // Müşteriye yapılan satışlar (Satış/Stok modülü) — modül pasifse boş kalır.
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get(`/customers/${id}`),
          api.get(`/storages?customer_id=${id}`),
        ]);
        setCustomer(c.data);
        setStorages(s.data);
      } catch (e) {
        notifyError(e);
        navigate("/musteriler");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (!salesStockEnabled) {
      setSales([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/sales-stock/sales?customer_id=${id}`);
        setSales(data);
      } catch (e) {
        // Modül erişimi kapanmışsa sessizce geç — emanet bilgileri çalışsın
        if (e?.response?.status !== 403) notifyError(e);
      }
    })();
  }, [id, salesStockEnabled]);

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  if (!customer) return null;

  const active = storages.filter((s) => s.status === "depoda");
  const past = storages.filter((s) => s.status === "teslim_edildi");
  const salesTotal = sales.reduce((acc, s) => acc + (s.total_gross || 0), 0);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="customer-detail-page">
      <Button asChild variant="ghost" size="sm" className="rounded-sm -ml-3" data-testid="back-btn">
        <Link to="/musteriler"><ArrowLeft className="h-4 w-4 mr-1" /> Müşteriler</Link>
      </Button>

      <Card className="rounded-sm border-border p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow text-amber-600">Müşteri Profili</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              {customer.full_name}
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5 text-sm">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span className="font-mono">{customer.phone}</span></div>
              {customer.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><span>{customer.email}</span></div>}
            </div>
            {(customer.vehicles?.length > 0 || customer.plate) && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="label-eyebrow mb-2">Araçlar</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(customer.vehicles?.length ? customer.vehicles : [{ plate: customer.plate, brand: customer.vehicle_brand, model: customer.vehicle_model }]).map((v, i) => (
                    <div key={v.id || i} className="flex items-center gap-3 border border-border rounded-sm p-2.5 text-sm" data-testid={`detail-vehicle-${i}`}>
                      <Car className="h-4 w-4 text-muted-foreground" />
                      <div>
                        {v.plate && <div className="font-mono font-bold tracking-wider">{v.plate}</div>}
                        {(v.brand || v.model) && <div className="text-xs text-muted-foreground">{[v.brand, v.model].filter(Boolean).join(" ")}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {customer.notes && (
              <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                {customer.notes}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="add-storage-for-customer">
              <Link to={`/emanetler?customer=${customer.id}`}>
                <Plus className="h-4 w-4 mr-2" /> Yeni Emanet
              </Link>
            </Button>
            {/* Yeni Satış — Satış/Stok ekranındaki stok çıkışı (Satış) formunu
                bu müşteri seçili olarak açar. "Yeni Emanet" ile aynı desen. */}
            {salesStockEnabled && (
              <Button asChild variant="outline" className="rounded-sm" data-testid="add-sale-for-customer">
                <Link to={`/satis-stok?customer=${customer.id}`}>
                  <ShoppingCart className="h-4 w-4 mr-2" /> Yeni Satış
                </Link>
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        <div className="label-eyebrow">Aktif Emanetler ({active.length})</div>
        {active.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground rounded-sm">
            Aktif emanet yok.
          </Card>
        ) : (
          active.map((s) => <StorageRow key={s.id} s={s} />)
        )}
      </div>

      <div className="space-y-3">
        <div className="label-eyebrow">Geçmiş ({past.length})</div>
        {past.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground rounded-sm">
            Geçmiş emanet yok.
          </Card>
        ) : (
          past.map((s) => <StorageRow key={s.id} s={s} past />)
        )}
      </div>

      {/* Müşteriye yapılan satışlar (Satış/Stok modülü) */}
      {salesStockEnabled && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="label-eyebrow">Satışlar ({sales.length})</div>
            {sales.length > 0 && (
              <div className="text-sm">
                <span className="text-muted-foreground">Toplam: </span>
                <span className="font-mono font-bold" data-testid="customer-sales-total">
                  {fmtMoney(salesTotal, defaultCurrency)}
                </span>
              </div>
            )}
          </div>
          {sales.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground rounded-sm">
              Bu müşteriye yapılmış satış yok.
            </Card>
          ) : (
            sales.map((s) => (
              <SaleRow key={s.id} sale={s} currency={defaultCurrency} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SaleRow({ sale, currency }) {
  const cur = sale.currency || currency;
  return (
    <Card className="rounded-sm border-border p-4" data-testid={`customer-sale-${sale.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <div className="h-10 w-10 bg-sky-600 text-white flex items-center justify-center rounded-sm shrink-0">
            <ShoppingCart className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">
              {(sale.items || []).length} kalem
              {sale.service_enabled ? " + hizmet bedeli" : ""}
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {new Date(sale.sale_date).toLocaleDateString("tr-TR")}
              {/* Satışta araç seçildiyse plaka gösterilir (revision 0005) */}
              {sale.vehicle_plate ? ` • ${sale.vehicle_plate}` : ""}
              {sale.actor_name ? ` • ${sale.actor_name}` : ""}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-lg tracking-tight">
            {fmtMoney(sale.total_gross, cur)}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono">
            KDV: {fmtMoney(sale.total_kdv, cur)}
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-border grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="label-eyebrow mb-1.5">Ürünler</div>
          <ul className="space-y-1">
            {(sale.items || []).map((it) => (
              <li key={it.id} className="flex justify-between gap-3">
                <span className="truncate">
                  {it.product_name}{" "}
                  <span className="text-muted-foreground font-mono">×{it.quantity}</span>
                </span>
                <span className="font-mono shrink-0">{fmtMoney(it.line_gross, cur)}</span>
              </li>
            ))}
            {sale.service_enabled && (
              <li className="flex justify-between gap-3 border-t border-border/60 pt-1">
                <span className="truncate">
                  Hizmet Bedeli
                  {sale.service_description ? ` — ${sale.service_description}` : ""}
                </span>
                <span className="font-mono shrink-0">{fmtMoney(sale.service_gross, cur)}</span>
              </li>
            )}
          </ul>
        </div>
        <div>
          <div className="label-eyebrow mb-1.5">Tahsilat</div>
          <ul className="space-y-1">
            {(sale.collections || []).map((c) => (
              <li key={c.id} className="flex justify-between gap-3">
                <span>{c.method_label || paymentMethodLabel(c.method)}</span>
                <span className="font-mono">{fmtMoney(c.amount, cur)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

function StorageRow({ s, past }) {
  return (
    <Link
      to={`/emanetler/${s.id}`}
      className="surface-card flex items-center gap-4 p-4 rounded-sm"
      data-testid={`storage-row-${s.id}`}
    >
      <div className={`h-10 w-10 flex items-center justify-center rounded-sm shrink-0 ${past ? "bg-muted text-muted-foreground" : "bg-foreground text-background"}`}>
        {past ? <PackageCheck className="h-5 w-5" /> : <PackageOpen className="h-5 w-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm">{s.brand} • {s.size}</div>
        <div className="text-xs text-muted-foreground font-mono truncate">
          {s.quantity} adet • {s.warehouse_name || "—"} / {s.location || "raf yok"} • {new Date(s.check_in_date).toLocaleDateString("tr-TR")}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5 shrink-0">
        <Badge className={`rounded-sm ${past ? "bg-muted text-muted-foreground" : "bg-amber-500 text-white hover:bg-amber-500"}`}>
          {past ? "Teslim" : "Depoda"}
        </Badge>
        {!past && s.warehouse_name && (
          <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
            {s.warehouse_name}
          </span>
        )}
      </div>
    </Link>
  );
}
